/**
 * AgentDefaultPolicySwitches — one agent kind's landlord-default enablement + review switches
 * (RA-TOGGLE-UI, per agent kind). Moved here from the Repairs page's former
 * LandlordDefaultAgentPolicySection; mounted inside each agent's card on Settings → AI agents.
 *
 * Each switch saves immediately via PATCH /landlord/api/agent-policy/default/:agentKind, with an
 * inline "Use default" link to reset that one field back to 'inherit', inline error + retry, and
 * the same loading / load-failed state machine as CaseAgentPolicySection.
 *
 * The reference agent (reference_coordinator) is an on/off switch only (BR-28): it renders just the
 * enablement row — no review row and no "Use default" link — and PATCHes only `enablement`.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Switch } from '../ui/switch';
import { getAgentDefaultPolicy, patchAgentDefaultPolicy } from '../../api/agent-playbooks.api';
import type {
  AgentEnablementDecision as AgentEnablement,
  AgentReviewDecision as AgentReview,
  AgentPolicyResponse,
  PolicyAgentKind,
} from '@resida/api-types/agent';

const ENABLEMENT_COPY: Record<
  PolicyAgentKind,
  { label: string; hint: string; saved?: { on: string; off: string } }
> = {
  repair: {
    label: 'Turn on for new repairs',
    hint: "Applies to repairs where you haven't set it on the repair itself.",
  },
  leasing: {
    label: 'Turn on for new leads',
    hint: "Applies to listings and leads where you haven't set it yourself.",
  },
  reference_coordinator: {
    label: 'Turn on reference checks',
    hint: "The reference agent contacts the referees you list. While it is off, reference checks can't be started.",
    saved: { on: 'Reference checks turned on.', off: 'Reference checks turned off.' },
  },
};

/** Kinds whose landlord default is an on/off switch only — no review switch, no "Use default" (BR-28). */
const ENABLEMENT_ONLY_KINDS: ReadonlySet<PolicyAgentKind> = new Set(['reference_coordinator']);

const REVIEW_COPY = {
  label: "Review messages before they're sent",
  hint: 'Each message waits for your approval.',
};

interface SwitchRowProps {
  id: string;
  testPrefix: string;
  label: string;
  hint: string;
  checked: boolean;
  overridden: boolean;
  saving: boolean;
  disabled: boolean;
  error: string | null;
  onChange: (next: boolean) => void;
  onReset: () => void;
  onRetry: () => void;
}

/** One switch + label row: the whole switch+label line is a >=44px tap target. */
function SwitchRow({
  id,
  testPrefix,
  label,
  hint,
  checked,
  overridden,
  saving,
  disabled,
  error,
  onChange,
  onReset,
  onRetry,
}: SwitchRowProps) {
  const captionId = `${id}-caption`;
  const isEnablement = testPrefix === 'landlord-default-agent-enablement';
  const switchProps = {
    id,
    checked,
    onCheckedChange: onChange,
    disabled: saving || disabled,
    'aria-describedby': captionId,
    className: 'mt-2.5',
  };
  const captionProps = { id: captionId, className: 'text-sm text-muted-foreground' };
  const captionBody = (
    <>
      {hint}
      {overridden && ' Overridden by you.'}
    </>
  );
  const resetProps = {
    type: 'button' as const,
    className: 'flex min-h-[44px] w-fit items-center text-sm text-primary hover:underline',
    onClick: onReset,
    disabled: saving,
  };
  return (
    <div>
      <div className="flex items-start gap-3">
        {/* Literal data-testid values (not template strings) so the RW-TH-7 closed-set extractor sees them. */}
        {isEnablement ? (
          <Switch data-testid="landlord-default-agent-enablement-switch" {...switchProps} />
        ) : (
          <Switch data-testid="landlord-default-agent-review-switch" {...switchProps} />
        )}
        <label
          htmlFor={id}
          className="flex min-h-[44px] flex-1 cursor-pointer items-center text-sm font-medium text-foreground"
        >
          {label}
        </label>
      </div>
      {isEnablement ? (
        <p data-testid="landlord-default-agent-enablement-caption" {...captionProps}>
          {captionBody}
        </p>
      ) : (
        <p data-testid="landlord-default-agent-review-caption" {...captionProps}>
          {captionBody}
        </p>
      )}
      {overridden && (
        isEnablement ? (
          <button data-testid="landlord-default-agent-enablement-reset" {...resetProps}>
            Use default
          </button>
        ) : (
          <button data-testid="landlord-default-agent-review-reset" {...resetProps}>
            Use default
          </button>
        )
      )}
      {error && (
        <div
          role="alert"
          data-testid={`${testPrefix}-error`}
          className="mt-1 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
        >
          <p>{error}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2 min-h-[44px]"
            data-testid={`${testPrefix}-retry`}
            onClick={onRetry}
            disabled={saving}
          >
            Retry
          </Button>
        </div>
      )}
    </div>
  );
}

export interface AgentDefaultPolicySwitchesProps {
  agentKind: PolicyAgentKind;
}

export function AgentDefaultPolicySwitches({ agentKind }: AgentDefaultPolicySwitchesProps) {
  const enablementOnly = ENABLEMENT_ONLY_KINDS.has(agentKind);
  const [enablement, setEnablement] = useState<AgentEnablement>('inherit');
  const [review, setReview] = useState<AgentReview>('inherit');
  const [resolvedEnablement, setResolvedEnablement] = useState<'enabled' | 'disabled'>('disabled');
  const [resolvedReview, setResolvedReview] = useState<'required' | 'not_required'>('required');
  const [savingEnablement, setSavingEnablement] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  // Load failure (whole section) and per-switch save failures (inline under the switch, with retry).
  const [error, setError] = useState<string | null>(null);
  const [enablementError, setEnablementError] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  // The last attempted save per switch, so Retry repeats exactly what failed.
  const lastEnablementAttempt = useRef<() => void>(() => {});
  const lastReviewAttempt = useRef<() => void>(() => {});
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  // True while the initial GET is in flight — switches are not shown during this period.
  const [loadingPolicy, setLoadingPolicy] = useState(true);
  // True if the most recent GET failed — prevents toggling against wrong defaults.
  const [loadFailed, setLoadFailed] = useState(false);
  const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applyEnablement = (p: AgentPolicyResponse) => {
    setEnablement(p.enablement);
    setResolvedEnablement(p.resolvedEnablement);
  };
  const applyReview = (p: AgentPolicyResponse) => {
    setReview(p.review);
    setResolvedReview(p.resolvedReview);
  };

  const loadPolicy = useCallback(
    async (isCancelled: () => boolean = () => false) => {
      setLoadingPolicy(true);
      setLoadFailed(false);
      setError(null);
      try {
        const stored = await getAgentDefaultPolicy(agentKind);
        if (isCancelled()) return;
        setEnablement(stored.enablement);
        setReview(stored.review);
        setResolvedEnablement(stored.resolvedEnablement);
        setResolvedReview(stored.resolvedReview);
      } catch (err) {
        if (isCancelled()) return;
        console.error(`Failed to load ${agentKind} default agent policy:`, err);
        setError('Could not load agent policy settings. Refresh the page to try again.');
        setLoadFailed(true);
      } finally {
        if (!isCancelled()) setLoadingPolicy(false);
      }
    },
    [agentKind],
  );

  useEffect(() => {
    let cancelled = false;
    void loadPolicy(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [loadPolicy]);

  useEffect(
    () => () => {
      if (successTimer.current) clearTimeout(successTimer.current);
    },
    [],
  );

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    if (successTimer.current) clearTimeout(successTimer.current);
    successTimer.current = setTimeout(() => setSuccessMsg(null), 3000);
  };

  const save = async (
    body: { enablement: AgentEnablement } | { review: AgentReview },
    setSaving: (v: boolean) => void,
    setSaveError: (v: string | null) => void,
    apply: (p: AgentPolicyResponse) => void,
    success: string,
    failure: string,
  ) => {
    setSaving(true);
    setSaveError(null);
    try {
      apply(await patchAgentDefaultPolicy(agentKind, body));
      showSuccess(success);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : failure);
    } finally {
      setSaving(false);
    }
  };

  const saveEnablement = (next: AgentEnablement, success: string) => {
    lastEnablementAttempt.current = () => void saveEnablement(next, success);
    return save(
      { enablement: next },
      setSavingEnablement,
      setEnablementError,
      applyEnablement,
      success,
      'Failed to update agent enablement',
    );
  };
  const saveReview = (next: AgentReview, success: string) => {
    lastReviewAttempt.current = () => void saveReview(next, success);
    return save(
      { review: next },
      setSavingReview,
      setReviewError,
      applyReview,
      success,
      'Failed to update human review setting',
    );
  };

  const enablementCopy = ENABLEMENT_COPY[agentKind];

  return (
    <div data-testid="landlord-default-agent-policy-section">
      {loadingPolicy ? (
        <div className="space-y-3" data-testid="landlord-default-agent-policy-loading" aria-busy="true">
          <Skeleton className="h-9 w-full sm:w-64" />
          {!enablementOnly && <Skeleton className="h-9 w-full sm:w-64" />}
        </div>
      ) : (
        <div className="space-y-4" data-testid="landlord-default-agent-policy-form">
          {/* Enablement */}
          <SwitchRow
            id={`landlord-default-agent-enablement-${agentKind}`}
            testPrefix="landlord-default-agent-enablement"
            label={enablementCopy.label}
            hint={enablementCopy.hint}
            checked={resolvedEnablement === 'enabled'}
            overridden={!enablementOnly && enablement !== 'inherit'}
            saving={savingEnablement}
            disabled={loadFailed}
            error={enablementError}
            onChange={(v) =>
              void saveEnablement(
                v ? 'enabled' : 'disabled',
                (v ? enablementCopy.saved?.on : enablementCopy.saved?.off) ?? 'Default agent enablement updated.',
              )
            }
            onReset={() =>
              void saveEnablement('inherit', 'Default agent enablement reset to system default.')
            }
            onRetry={() => lastEnablementAttempt.current()}
          />

          {/* Review gate — absent for an enablement-only kind (BR-28) */}
          {!enablementOnly && (
            <SwitchRow
              id={`landlord-default-agent-review-${agentKind}`}
              testPrefix="landlord-default-agent-review"
              label={REVIEW_COPY.label}
              hint={REVIEW_COPY.hint}
              checked={resolvedReview === 'required'}
              overridden={review !== 'inherit'}
              saving={savingReview}
              disabled={loadFailed}
              error={reviewError}
              onChange={(v) =>
                void saveReview(v ? 'required' : 'not_required', 'Default human review setting updated.')
              }
              onReset={() =>
                void saveReview('inherit', 'Default human review setting reset to system default.')
              }
              onRetry={() => lastReviewAttempt.current()}
            />
          )}

          {error && (
            <div
              role="alert"
              data-testid="landlord-default-agent-policy-error"
              className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
            >
              <p>{error}</p>
              {loadFailed && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2 min-h-[44px]"
                  data-testid="landlord-default-agent-policy-retry"
                  onClick={() => void loadPolicy()}
                >
                  Retry
                </Button>
              )}
            </div>
          )}

          {/* Fixed-height live region so the 3s confirmation never shifts the card layout. */}
          <div
            className="min-h-5 text-sm text-success"
            role="status"
            aria-live="polite"
            data-testid="landlord-default-agent-policy-success-region"
          >
            {successMsg && <span data-testid="landlord-default-agent-policy-success">{successMsg}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentDefaultPolicySwitches;
